import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import {
  Button,
  Card,
  CardBody,
  Input,
  Badge,
  Col,
  Row,
  PopoverBody,
  UncontrolledPopover,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import Select, { components } from "react-select";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_BADGE_COLORS,
  BATCH_STATUS_OPTIONS, } from "components/constant";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getBatchList, getInstructorBatchList } from "action/batch";
import { useDispatch, useSelector } from "react-redux";
import { batchList } from "constants/batch";
import { formatDate } from "components/CommonComponents/CommonFunctions";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { getInstructorList } from "action/instructor";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

function CourseSchedule({ courseId, deliveryMode = "", }) {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    searchSession: "",
    searchInstructor: "",
    sessionDate: [null, null],
    registrationDate: [null, null],
    status: null,
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [copiedLink, setCopiedLink] = useState(null);

  const batchListData = useSelector((state) => state.batch.batchList);
  const instructorListData = useSelector(
    (state) => state.instructor.instructorList,
  );
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const isOnlineOrHybrid = deliveryMode === "Online" || deliveryMode === "Offline, Online" || deliveryMode === "Online, Offline";
  const isOfflineOrHybrid = deliveryMode === "Offline" || deliveryMode === "Offline, Online" || deliveryMode === "Online, Offline";

  useEffect(() => {
    const baseFilter = [
      {
        field: "CourseID",
        condition: "eq",
        filterValues: [String(courseId)],
      },
    ];

    const filters = searchState.filterParams?.length
      ? searchState.filterParams
      : JSON.stringify(baseFilter);

    if (role === "TRAINER") {
      getInstructorBatchList(dispatch, pageNum, filters);
    } else {
      getBatchList(dispatch, pageNum, filters);
    }
    return () => {
      dispatch({ type: batchList, payload: {} });
    };
  }, [dispatch, pageNum, courseId, searchState.filterParams]);

  useEffect(() => {
    if (batchListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: batchListData?.data?.Batches?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (batchListData?.code || batchListData?.message) {
      setListState({
        isLoading: false,
        error:
          batchListData?.message ||
          "Failed to load batch list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [batchListData]);

  //get the instructor or trainer list
  useEffect(() => {
    if (role !== "TRAINER") {
      getInstructorList(dispatch);
    }
  }, [dispatch]);

  const instructorOptions =
    instructorListData?.data?.trainers?.map((trainer) => ({
      label: trainer.user?.fullName,
      value: trainer.ID,
    })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    filterParams.push({
      field: "CourseID",
      condition: "eq",
      filterValues: [String(courseId)],
    });

    if (searchState.searchSession?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [searchState.searchSession.trim()],
      });
    }

    if (searchState.searchInstructor?.value) {
      filterParams.push({
        field: "TrainerName",
        condition: "eq",
        filterValues: [searchState.searchInstructor.label],
      });
    }

    if (searchState.status?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });
    }

    if (searchState.sessionDate?.[0]) {
      if (searchState.sessionDate?.[1]) {
        filterParams.push({
          field: "BatchStartDate",
          condition: "btw",
          filterValues: [
            formatDate(searchState.sessionDate[0]),
            formatDate(searchState.sessionDate[1]),
          ],
        });
      } else {
        filterParams.push({
          field: "BatchStartDate",
          condition: "eq",
          filterValues: [formatDate(searchState.sessionDate[0])],
        });
      }
    }

    if (searchState.registrationDate?.[0]) {
      if (searchState.registrationDate?.[1]) {
        filterParams.push({
          field: "RegistrationStartDate",
          condition: "btw",
          filterValues: [
            formatDate(searchState.registrationDate[0]),
            formatDate(searchState.registrationDate[1]),
          ],
        });
      } else {
        filterParams.push({
          field: "RegistrationStartDate",
          condition: "eq",
          filterValues: [formatDate(searchState.registrationDate[0])],
        });
      }
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setSearchState({
      searchSession: "",
      searchInstructor: "",
      sessionDate: [null, null],
      registrationDate: [null, null],
      status: null,
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const tableData =
    batchListData?.data?.Batches?.map((batch, index) => ({
      id: index + 1,
      batchId: batch.ID,
      sessionName: batch.name,
      trainingCenterName: batch.trainingCenters?.name || "-",
      address: batch.trainingCenters?.address || "-",
      status: batch.status || "-",
      instructorName:
        batch.trainers
          ?.map((trainer) =>
            [trainer.user?.firstName, trainer.user?.lastName]
              .filter(Boolean)
              .join(" "),
          )
          .filter(Boolean) || [],
      sessionStartDate: batch.batchStartDate,
      sessionEndDate: batch.batchEndDate,
      registrationStartDate: batch.registrationStartDate,
      registrationEndDate: batch.registrationEndDate,
      sessionStartTime: batch.startTime,
      sessionEndTime: batch.endTime,
      virtualMeetingUrl: batch.virtualMeetingUrl || "-",
      enrollmentCount: batch.enrolledUserCount,
      // description: batch.description?.replace(/<[^>]*>/g, "") || "-",
      duration: batch.duration,
      batchEnrollmentURLTrainee: batch.batchEnrollmentURLTrainee || "",
      batchEnrollmentURLChannelPartner: batch.batchEnrollmentURLChannelPartner || "",
    })) || [];

  const handleNavigateToEnrollment = (rowOriginal) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", "enrollment");

    const state = {
      batchId: urlEncodedId(rowOriginal.batchId),
    };
    if (rowOriginal.sessionName) {
      state.batchName = rowOriginal.sessionName;
    }

    navigate(`${location.pathname}?${params.toString()}`, { state });
  };

  const handleNavigateToBatchDetails = (batchId) => {
    const encodedId = urlEncodedId(batchId);
    let basePath = "/admin";
    if (role === "SUPER_ADMIN") basePath = "/super-admin";
    else if (role === "CHANNEL_PARTNER") basePath = "/channel-partner";
    else if (role === "TRAINER") basePath = "/instructor";

    navigate(`${basePath}/batch/${encodedId}/details`, {
      state: { fromCourseSchedule: true }
    });
  };

  const handleCopyEnrollmentLink = async (url, batchId, type) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);

      setCopiedLink({
        batchId,
        type,
      });

      setTimeout(() => {
        setCopiedLink(null);
      }, 2000);
    } catch (error) {
      notify("warning", "Failed to copy enrollment link");
    }
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={STATUS_BADGE_COLORS[props.data.value] || "secondary"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={STATUS_BADGE_COLORS[props.data.value] || "secondary"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
      centerAlign: true,
      minWidth: 80,
      maxWidth: 80,
    },
    {
      Header: "Batch Name",
      accessor: "sessionName",
      minWidth: 140,
      maxWidth: 140,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Instructor",
      accessor: "instructorName",
      minWidth: 170,
      maxWidth: 170,
      Cell: ({ row }) => {
        const trainers = (row.original.instructorName || []).filter((trainer) =>
          trainer?.trim(),
        );

        const { batchId } = row.original;

        if (!trainers.length) {
          return "—";
        }

        const visibleTrainers = trainers.slice(0, 3);
        const remainingCount = trainers.length - 3;
        const trainerPopoverId = `trainer-${batchId}`;

        return (
          <div className="text-wrapper">
            {visibleTrainers.map((trainer, index) => (
              <div className="trainer-name" key={index}>
                <div className="trainer-row">
                  <span className="trainer-bullet">•</span>
                  <span className="trainer-text">{trainer}</span>
                </div>

                {index === 2 && remainingCount > 0 && (
                  <>
                    <div className="trainer-count">
                      <Badge
                        id={trainerPopoverId}
                        color="primary"
                        pill
                        className="cursor-pointer"
                      >
                        +{remainingCount}
                      </Badge>
                    </div>

                    <UncontrolledPopover
                      target={trainerPopoverId}
                      trigger="hover"
                      placement="top"
                    >
                      <PopoverBody>
                        <div className="trainer-popover-list">
                          {trainers.map((trainer, trainerIndex) => (
                            <div key={trainerIndex}>• {trainer}</div>
                          ))}
                        </div>
                      </PopoverBody>
                    </UncontrolledPopover>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    ...(isOnlineOrHybrid
      ? [
          {
            Header: "Virtual Meeting Link",
            accessor: "virtualMeetingUrl",
            minWidth: 185,
            maxWidth: 185,
            Cell: ({ value }) => {
              if (!value || value === "-")
                return <div className="text-wrapper text-center">-</div>;
              return (
                <div
                  className="text-wrapper text-center"
                  title="Join Virtual Meeting"
                >
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary rounded-circle"
                  >
                    <i className="fa fa-video-camera" />
                  </a>
                </div>
              );
            },
          },
        ]
      : []),
    ...(isOfflineOrHybrid
      ? [
          {
            Header: "Training Center Name",
            accessor: "trainingCenterName",
            minWidth: 180,
            maxWidth: 180,
            Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
          },
          {
            Header: "Training Center Address",
            accessor: "address",
            minWidth: 220,
            maxWidth: 220,
            Cell: ({ row }) => {
              const { address, batchId } = row.original;
              if (!address || address === "-") return "-";

              const truncatedAddress =
                address.length > 80
                  ? `${address.substring(0, 80)}...`
                  : address;

              return (
                <>
                  <span id={`addr-${batchId}`} className="ellipsis-text">
                    {truncatedAddress}
                  </span>
                  {address.length > 80 && (
                    <UncontrolledPopover
                      target={`addr-${batchId}`}
                      trigger="hover"
                      placement="top"
                    >
                      <PopoverBody>{address}</PopoverBody>
                    </UncontrolledPopover>
                  )}
                </>
              );
            },
          },
        ]
      : []),
    {
      Header: "Registration Dates",
      centerAlign: true,
      minWidth: 125,
      maxWidth: 125,
      Cell: ({ row }) => {
        const { registrationStartDate, registrationEndDate } = row.original;
        if (!registrationStartDate || !registrationEndDate) return "-";

        return (
          <div className="text-wrapper">
            <div>{registrationStartDate}</div>
            <div className="font-weight-bold text-center">to</div>
            <div>{registrationEndDate}</div>
          </div>
        );
      },
    },
    {
      Header: "Batch Dates",
      centerAlign: true,
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ row }) => {
        const { sessionStartDate, sessionEndDate } = row.original;
        if (!sessionStartDate || !sessionEndDate) return "-";

        return (
          <div className="text-wrapper">
            <div>{sessionStartDate}</div>
            <div className="font-weight-bold text-center">to</div>
            <div>{sessionEndDate}</div>
          </div>
        );
      },
    },
    {
      Header: "Time",
      centerAlign: true,
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ row }) => {
        const { sessionStartTime, sessionEndTime } = row.original;
        if (!sessionStartTime || !sessionEndTime) return "-";

        return (
          <div className="text-wrapper">
            <div>{sessionStartTime}</div>
            <div className="font-weight-bold text-center">to</div>
            <div>{sessionEndTime}</div>
          </div>
        );
      },
    },
    {
      Header: "Duration",
      accessor: "duration",
      minWidth: 140,
      maxWidth: 140,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    // {
    //   Header: "Description",
    //   accessor: "description",
    //   minWidth: 160,
    //   maxWidth: 180,
    //   Cell: ({ row }) => {
    //     const { description, id } = row.original;

    //     if (!description) return "-";

    //     const truncatedDescription =
    //       description.length > 80
    //         ? `${description.substring(0, 80)}...`
    //         : description;

    //     return (
    //       <>
    //         <span id={`desc-${id}`} className="ellipsis-text">
    //           {truncatedDescription}
    //         </span>

    //         <UncontrolledPopover
    //           target={`desc-${id}`}
    //           trigger="hover"
    //           placement="top"
    //         >
    //           <PopoverBody>{description}</PopoverBody>
    //         </UncontrolledPopover>
    //       </>
    //     );
    //   },
    // },
    {
      Header: "Enrollment Link",
      accessor: "BatchEnrollmentURL",
      minWidth: 220,
      maxWidth: 220,

      Cell: ({ row }) => {
        const batchId = row.original.id;

        const traineeURL = row.original.batchEnrollmentURLTrainee;

        const channelPartnerURL = row.original.batchEnrollmentURLChannelPartner;

        const traineeCopied =
          copiedLink?.batchId === batchId && copiedLink?.type === "trainee";

        const channelPartnerCopied =
          copiedLink?.batchId === batchId &&
          copiedLink?.type === "channelPartner";

        return (
          <div className="d-flex flex-column align-items-start">
            <div className="mb-1 text-wrapper">
              <span className="mr-2">Trainee:</span>

              {traineeCopied ? (
                <span className="text-success font-weight-bold">
                  <i className="fa fa-check mr-1" />
                  Link Copied!
                </span>
              ) : (
                <Button
                  color="link"
                  className="p-0 xxs-text"
                  disabled={!traineeURL}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleCopyEnrollmentLink(traineeURL, batchId, "trainee");
                  }}
                  title="Copy Trainee Enrollment Link"
                >
                  <i className="fa fa-link mr-1" />
                  Copy Link
                </Button>
              )}
            </div>

            <div className="text-wrapper">
              <span className="mr-2">Channel Partner:</span>

              {channelPartnerCopied ? (
                <span className="text-success font-weight-bold">
                  <i className="fa fa-check mr-1" />
                  Link Copied!
                </span>
              ) : (
                <Button
                  color="link"
                  className="p-0 xxs-text"
                  disabled={!channelPartnerURL}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleCopyEnrollmentLink(
                      channelPartnerURL,
                      batchId,
                      "channelPartner",
                    );
                  }}
                  title="Copy Channel Partner Enrollment Link"
                >
                  <i className="fa fa-link mr-1" />
                  Copy Link
                </Button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      Header: "Enrollments",
      accessor: "enrollmentCount",
      minWidth: 90,
      maxWidth: 90,
      centerAlign: true,
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center align-items-center">
          <Button
            color="link"
            className="p-0"
            onClick={() => handleNavigateToEnrollment(row.original)}
            title="View Enrollments"
          >
            <i className="fa fa-eye" />
          </Button>
          <span className="mr-2">{row.original.enrollmentCount || 0}</span>
        </div>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ value }) => (
        <Badge color={STATUS_BADGE_COLORS[value] || "secondary"} pill>
          {value || "-"}
        </Badge>
      ),
    },
    {
      Header: "Action",
      minWidth: 80,
      maxWidth: 80,
      centerAlign: true,
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center align-items-center">
          <Button
            color="link"
            className="p-0"
            onClick={() => handleNavigateToBatchDetails(row.original.batchId)}
            title="View Batch Details"
          >
            <i className="fa fa-external-link-alt" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Row>
        <Col md="12">
          <Card className="course-schedule-card">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                {/* Left side - Filter Inputs + Search */}
                <div className="d-flex flex-wrap align-items-center filter-group">
                  <Input
                    type="text"
                    placeholder="Search by batch name "
                    className="mr-2 mb-2 filter-input-large"
                    value={searchState.searchSession}
                    onChange={(e) =>
                      setSearchState((prev) => ({
                        ...prev,
                        searchSession: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  {role !== "TRAINER" && (
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={instructorOptions}
                      placeholder="Select instructor"
                      value={searchState.searchInstructor}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchInstructor: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      onKeyDown={handleKeyDown}
                    />
                  )}

                  <DatePicker
                    selected={searchState.registrationDate[0]}
                    startDate={searchState.registrationDate[0]}
                    endDate={searchState.registrationDate[1]}
                    onChange={(dates) =>
                      setSearchState((prev) => ({
                        ...prev,
                        registrationDate: dates,
                      }))
                    }
                    selectsRange
                    isClearable={!!searchState.registrationDate[0]}
                    placeholderText="Select registration date or range"
                    className="form-control mr-2 mb-2 filter-input-large"
                    dateFormat="dd-MMM-yyyy"
                    showMonthDropdown
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    calendarClassName="custom-calendar"
                    onPaste={(e) => e.preventDefault()}
                    popperClassName="datepicker-popper"
                  />

                  <DatePicker
                    selected={searchState.sessionDate[0]}
                    startDate={searchState.sessionDate[0]}
                    endDate={searchState.sessionDate[1]}
                    onChange={(dates) =>
                      setSearchState((prev) => ({
                        ...prev,
                        sessionDate: dates,
                      }))
                    }
                    selectsRange
                    isClearable={!!searchState.sessionDate[0]}
                    placeholderText="Select batch date or range"
                    className="form-control mr-2 mb-2 filter-input-large"
                    dateFormat="dd-MMM-yyyy"
                    showMonthDropdown
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    calendarClassName="custom-calendar"
                    onPaste={(e) => e.preventDefault()}
                    popperClassName="datepicker-popper"
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={BATCH_STATUS_OPTIONS}
                    placeholder="Select status"
                    value={searchState.status}
                    onChange={(selected) =>
                      setSearchState((prev) => ({
                        ...prev,
                        status: selected,
                      }))
                    }
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    components={{ Option, SingleValue }}
                    onKeyDown={handleKeyDown}
                  />

                  <Button
                    color="default"
                    onClick={handleSearch}
                    className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                      listState.showLoaderOnClick ? "search-btn" : ""
                    }`}
                  >
                    {listState.showLoaderOnClick ? (
                      <i className="fa fa-spinner fa-spin loader" />
                    ) : (
                      <>
                        <i className="fa fa-search mr-1" />
                        Search
                      </>
                    )}
                  </Button>
                  {searchState.filterParams.length > 0 && (
                    <Button
                      color="default"
                      className="mb-2 filter-btn btn-simple btn-deep-blue"
                      onClick={handleClear}
                    >
                      <i className="fa fa-times mr-1" /> Clear
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 mb-3 mx-1">
                {batchListData?.data?.totalBatchesRecords > 0 ? (
                  <>
                    {searchState.filterParams.length > 0 ? (
                      <span className="total-records">
                        Showing {batchListData?.data?.totalFilteredRecords} of{" "}
                        {batchListData?.data?.totalBatchesRecords} Records
                      </span>
                    ) : (
                      <span className="total-records">
                        Total Records :{" "}
                        {batchListData?.data?.totalBatchesRecords}
                      </span>
                    )}
                  </>
                ) : null}
              </div>

              {listState.isLoading ? (
                <div className="loaderContainer-sm ">
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </div>
              ) : listState.error ? (
                <div className="loaderContainer-sm  error-style">
                  <div className="error-icon">
                    <i class="fa-solid fa-circle-exclamation"></i>
                  </div>
                  <span>{listState.error}</span>
                </div>
              ) : listState.noRecords ? (
                <div className="loaderContainer-sm  empty-style">
                  <div className="empty-icon">
                    <i class="fa-solid fa-circle-info"></i>
                  </div>
                  <span> No batches found.</span>
                </div>
              ) : (
                <CommonTable
                  columns={columns}
                  data={tableData || []}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalPages={Math.ceil(
                    (batchListData?.data?.totalFilteredRecords || 0) /
                      (batchListData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE),
                  )}
                  showPaginationBottom
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default CourseSchedule;
